<template>
  <SkeletonTableLoader :items-length="paymentMilestones.length">
    <v-container>
      <v-row justify="end" align-content="end">
        <v-col cols="3" v-if="remainingValue > 0">
          <v-btn
            rounded
            dense
            small
            color="primary"
            @click="handleAddPaymentModal"
            :disabled="!canUserEdit"
          >
            <v-icon> mdi-plus </v-icon>
            {{ $t('payment.milestone.addMilestone') }}
          </v-btn>
        </v-col>
      </v-row>
      <PaymentMilestonesTable
        v-model="paymentMilestones"
        :contractPrice="contract.signedPriceCentsPerCurrency"
        :base-currency="contract.currencies[0]"
        :payment-applications="paymentApplications"
        :remaining-value="remainingValue"
        @milestones-update="$emit('milestones-update', $route.params.contractId)"
        @delete-payment="handleDeletePaymentMilestone"
        @update-submilestones="handleUpdateSubmilestones"
        @update-submilestone="handleUpdateSubmilestone"
        @delete-submilestone="handleDeleteSubmilestone"
        @add-application="(data) => $emit('add-application', data)"
        @mandatorySubmilestones="handleMandatorySubMilestones"
        :parts="partsList"
        :chosen-currency="contract.chosenCurrency"
        :currencies="contract.currencies"
        :is-multi-currency="contract.isMultiCurrency"
      />
      <v-row class="mx-1">
        <v-btn
          v-if="isProjectController"
          rounded
          dense
          small
          color="success"
          @click="handleExportMilestones"
          :disabled="paymentMilestones.length === 0"
        >
          <v-icon small class="mr-1">mdi-file-excel-outline</v-icon>
          {{ $t('payment.milestone.export') }}
        </v-btn>
        <v-spacer />
        <v-btn
          rounded
          dense
          small
          color="success"
          @click="handleOpenImportMilestones"
          :disabled="!canUserEdit"
        >
          <v-icon small class="mr-1">mdi-file-excel-outline</v-icon>
          {{ $t('payment.milestone.import') }}
        </v-btn>
      </v-row>
    </v-container>
  </SkeletonTableLoader>
</template>

<script>
import {
  updatePaymentSubMilestone,
  addPaymentMilestone,
  updatePaymentMilestone,
  deletePaymentMilestone,
  saveSubMilestones,
  deletePaymentSubMilestone,
  calculateMaxAmountsForPaymentForm,
  checkMilestonesNamesDuplicates,
  exportScheduleToExcel
} from '@/services'
import { mapState } from 'vuex'
import { DEFAULT_CONTRACT } from '@/helpers'
import { authContext } from '@/plugins/msalPlugin'

export default {
  name: 'PaymentMilestones',
  props: {
    contract: {
      type: Object,
      default: function () {
        return DEFAULT_CONTRACT
      }
    },
    paymentMilestones: {
      type: Array,
      default: function () {
        return []
      }
    },
    paymentApplications: Array
  },
  data () {
    return {
      expandedItems: []
    }
  },
  computed: {
    partsList () {
      return this.contract.parts.map(part => {
        const currencyData = this.contract.currencies.find(i => i.key === part.currency) || this.contract.currencies.find(i => i.value === 1)
        const assignedValue = this.paymentMilestones.filter(i => i.partId === part.id && !i.variationId && !i.liquidatedDamageId).reduce((sum, milestone) => sum + milestone.paymentAmountCents, 0) +
          this.paymentMilestones.filter(i => i.partId === null && !i.variationId && !i.liquidatedDamageId).reduce((sum, milestone) => {
            if (milestone.multiCurrencyMilestone) {
              return sum + milestone.subMilestones.filter(i => i.part.id === part.id).reduce((sum, subMilestone) => sum + subMilestone.paymentAmountCents, 0)
            }
            return sum + ((milestone.paymentAmountCents.sum || milestone.paymentAmountCents) * (part.ratio / 100))
          }, 0)
        return {
          ...part,
          currencyData,
          remainingValue: part.signedPriceCents - assignedValue,
          formattedRemainingValue: this.$options.filters.formatCurrency(part.signedPriceCents - assignedValue, { key: part.currency, value: 1 }),
          formattedPrice: part.signedPriceCents
            ? this.$options.filters.formatCurrency(part.signedPriceCents, { key: part.currency, value: 1 })
            : null
        }
      })
    },
    ...mapState({
      canUserEdit: state => state.userPermissionsToContract.canUserEdit
    }),
    contractPrice () {
      return this.contract.signedPriceCentsPerCurrency
    },
    remainingValue () {
      const remainingSum = this.partsList.reduce((sum, part) => sum + (part.remainingValue * part.currencyData.value), 0)
      return remainingSum
    },
    isProjectController () {
      const username = authContext.account.username.split('@')[0]
      return username === this.contract.projectController
    }
  },
  methods: {
    handleOpenImportMilestones () {
      this.$eventBus.$emit('OPEN_MODAL', {
        component: 'MilestonesImport',
        options: { maxWidth: 1050 },
        scope: {
          contract: this.contract,
          milestones: this.paymentMilestones
        },
        handlers: {
          FETCH_MILESTONES: () => this.$emit('milestones-update', this.$route.params.contractId)
        }
      })
    },
    handleExportMilestones () {
      exportScheduleToExcel(this.paymentMilestones, this.contract.contractName)
    },
    async createOrUpdatePaymentMilestone (data) {
      const milestone = data.id
        ? await updatePaymentMilestone(data)
        : await addPaymentMilestone(this.$route.params.contractId, data)
      this.$emit('milestones-update', this.$route.params.contractId)
      return milestone
    },
    async handleUpdateSubmilestone (data) {
      await updatePaymentSubMilestone(data)
      this.$emit('milestones-update', this.$route.params.contractId)
    },
    async handleUpdateSubmilestones ({ milestone, submilestones }) {
      await saveSubMilestones(this.$route.params.contractId, milestone, submilestones)
      this.$emit('milestones-update', this.$route.params.contractId)
    },
    async handleDeletePaymentMilestone (data) {
      this.$eventBus.$emit('OPEN_CONFIRM_MODAL', {
        options: { maxWidth: 650 },
        scope: {
          message: this.$t('common.deleteConfirm', { name: data.name })
        },
        handlers: {
          yes: async () => {
            await deletePaymentMilestone(data)
            this.$emit('milestones-update', this.$route.params.contractId)
          }
        }
      })
    },
    async handleDeleteSubmilestone (data) {
      this.$eventBus.$emit('OPEN_CONFIRM_MODAL', {
        scope: {
          message: this.$t('common.deleteConfirm', { name: data.name })
        },
        handlers: {
          yes: async () => {
            await deletePaymentSubMilestone(data)
            this.$emit('milestones-update', this.$route.params.contractId)
          }
        }
      })
    },
    checkNameDuplicate (milestone) {
      return checkMilestonesNamesDuplicates(milestone, this.paymentMilestones)
    },
    async savePaymentAndParts ({ milestone, submilestones }) {
      const newMilestone = await this.createOrUpdatePaymentMilestone(milestone)
      await this.handleUpdateSubmilestones({ milestone: newMilestone, submilestones })
    },
    handleMandatorySubMilestones (milestone) {
      this.$eventBus.$emit('OPEN_MODAL', {
        component: 'SubMilestonesValuesForm',
        options: { maxWidth: 1200 },
        scope: {
          payment: milestone,
          paymentApplications: this.paymentApplications,
          contract: this.contract,
          parts: this.partsList,
          currencies: this.contract.currencies,
          isMultiCurrency: this.contract.isMultiCurrency
        },
        handlers: {
          saveSubPayments: async (submilestones) => {
            await this.savePaymentAndParts({ milestone, submilestones })
            this.$eventBus.$emit('CLOSE_MODAL')
          },
          cancel: () => {
            this.$eventBus.$emit('CLOSE_MODAL')
          }
        }
      })
    },
    handleAddPaymentModal () {
      const { maxAmountsPerCurrency, maxAmountsPerPart } = calculateMaxAmountsForPaymentForm(
        this.paymentMilestones,
        this.contractPrice,
        this.contract.currencies,
        this.partsList
      )
      this.$eventBus.$emit('OPEN_MODAL', {
        component: 'PaymentMilestoneForm',
        options: { maxWidth: 850 },
        scope: {
          title: this.$t('payment.milestone.addMilestone'),
          maxAmountsPerCurrency,
          maxAmountsPerPart,
          contractPrice: this.contract.signedPriceCentsPerCurrency,
          currencies: this.contract.currencies,
          isVariation: false,
          parts: this.partsList,
          checkNameDuplicate: this.checkNameDuplicate,
          isMultiCurrency: this.contract.isMultiCurrency
        },
        handlers: {
          savePayment: this.createOrUpdatePaymentMilestone,
          mandatorySubmilestones: this.handleMandatorySubMilestones
        }
      })
    }
  }
}
</script>
